import { GATE } from '@/config/audio';
import { DEFAULTS, RHYTHM } from '@/config/training';
import type { AudioEngine } from '@/audio/engine/AudioEngine';
import { Metronome, type BeatInfo } from '@/audio/metronome/Metronome';
import type { SessionPhase, SessionSummary, StrumPattern, TimingVerdict } from '@/types/training';
import { Observable } from '../observable';
import { summarize } from '../summary';
import { TimingMatcher } from '../timingMatcher';

export interface StrummingSettings {
  patternId: string;
  chordId: string;
  bpm: number;
  durationMin: number;
  clickEnabled: boolean;
}

export interface StrummingSnapshot {
  phase: SessionPhase;
  countInBeat: number;
  /** Position in the eighth-note grid, 0..7, or -1 before the bar starts. */
  currentEighth: number;
  bpm: number;
  elapsedSec: number;
  totalSec: number;
  lastVerdict: TimingVerdict | null;
  score: number;
  strokes: number;
  summary: SessionSummary | null;
}

/**
 * Strumming trainer.
 *
 * The pattern's arrows are an **instruction for the hand**, not something the
 * analyser tries to verify: separating a downstroke from an upstroke means
 * resolving the order of six string onsets inside a ~20 ms window through a
 * phone microphone, which is not reliable (docs/research.md §3). What is
 * measured — honestly — is whether a stroke happened and when.
 *
 * Every stroke of the pattern is scheduled on the eighth-note grid; the hand
 * keeps moving through the rests, which is how strumming is actually taught.
 */
export class StrummingEngine {
  readonly store: Observable<StrummingSnapshot>;

  private metronome: Metronome | null = null;
  private readonly matcher = new TimingMatcher(RHYTHM.hitWindowMaxMs / 1000);
  private unsubscribeOnset: (() => void) | null = null;
  private unsubscribeBeat: (() => void) | null = null;
  private countInRemaining = DEFAULTS.countInBeats;
  private practisedSec = 0;
  private lastBeatTime = 0;
  private startedAt = Date.now();
  private rafId: number | null = null;
  private strokeIndex = 0;
  private finishing = false;

  constructor(
    private readonly audio: AudioEngine,
    private readonly pattern: StrumPattern,
    private readonly settings: StrummingSettings,
    private readonly onFinished?: (summary: SessionSummary) => void,
  ) {
    this.store = new Observable<StrummingSnapshot>({
      phase: 'idle',
      countInBeat: 0,
      currentEighth: -1,
      bpm: settings.bpm,
      elapsedSec: 0,
      totalSec: settings.durationMin * 60,
      lastVerdict: null,
      score: 0,
      strokes: 0,
      summary: null,
    });
  }

  get snapshot(): StrummingSnapshot {
    return this.store.getSnapshot();
  }

  start(): void {
    const context = this.audio.audioContext;
    if (!context) throw new Error('AudioEngine has no context');

    this.startedAt = Date.now();
    this.metronome = new Metronome(context);
    this.metronome.configure({ bpm: this.settings.bpm, beatsPerBar: 4, audible: this.settings.clickEnabled });
    // Strokes fall on eighths, so the attribution window is half a beat wide.
    this.matcher.setBeatInterval(30 / this.settings.bpm);
    // A gated onset can arrive up to eventDecisionSec after it was struck;
    // keep its beat pending until then (see TimingMatcher.setResolveLag).
    this.matcher.setResolveLag(GATE.eventDecisionSec);

    this.audio.setStringAnalysis(false);
    this.audio.setTarget(null);
    this.unsubscribeOnset = this.audio.onOnset((onset) => this.matcher.addOnset(onset.time));
    this.unsubscribeBeat = this.metronome.onBeat(this.handleBeat);

    this.countInRemaining = DEFAULTS.countInBeats;
    this.store.update({ phase: 'countIn', countInBeat: 1 });
    this.metronome.start();
    this.pump();
  }

  pause(): void {
    if (this.snapshot.phase !== 'running' && this.snapshot.phase !== 'countIn') return;
    this.metronome?.stop();
    // Strokes already scheduled will never arrive now; do not score them.
    this.matcher.dropPending();
    this.store.update({ phase: 'paused', currentEighth: -1 });
  }

  resume(): void {
    if (this.snapshot.phase !== 'paused' || !this.metronome) return;
    this.countInRemaining = DEFAULTS.countInBeats;
    // The audio clock kept running while paused; see RhythmEngine.resume().
    this.lastBeatTime = 0;
    this.store.update({ phase: 'countIn', countInBeat: 1 });
    this.metronome.start();
  }

  finish(): void {
    if (this.finishing) return;
    this.finishing = true;
    this.metronome?.stop();
    this.dispose();

    const summary = summarize({
      mode: 'strumming',
      startedAt: this.startedAt,
      durationSec: Math.round(this.practisedSec),
      chordIds: [this.settings.chordId],
      bpmStart: this.settings.bpm,
      bpmEnd: this.settings.bpm,
      attempts: [],
      transitions: [],
      rhythmScore: this.matcher.score(),
      clarityScore: null,
    });
    this.store.update({ phase: 'finished', summary, score: this.matcher.score() });
    this.onFinished?.(summary);
  }

  dispose(): void {
    this.unsubscribeOnset?.();
    this.unsubscribeBeat?.();
    this.unsubscribeOnset = null;
    this.unsubscribeBeat = null;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  private handleBeat = (beat: BeatInfo): void => {
    if (this.finishing) return;

    if (this.countInRemaining > 0) {
      // Counted up: a count-in is "one, two, three, four", not a countdown.
      const beatNumber = DEFAULTS.countInBeats - this.countInRemaining + 1;
      this.store.update({ phase: 'countIn', countInBeat: beatNumber, currentEighth: -1 });
      this.countInRemaining--;
      return;
    }

    if (this.lastBeatTime > 0) this.practisedSec += beat.time - this.lastBeatTime;
    this.lastBeatTime = beat.time;

    const halfBeat = 30 / this.settings.bpm;
    // Schedule the two eighths this quarter-note beat covers.
    for (const offset of [0, 1]) {
      const eighth = beat.beatInBar * 2 + offset;
      const step = this.pattern.steps.find((s) => s.eighth === eighth && !s.rest);
      if (!step) continue;
      this.matcher.expect(this.strokeIndex++, beat.time + offset * halfBeat);
    }

    this.store.update({
      phase: 'running',
      countInBeat: 0,
      currentEighth: beat.beatInBar * 2,
      elapsedSec: this.practisedSec,
    });

    if (this.practisedSec >= this.settings.durationMin * 60) this.finish();
  };

  private pump = (): void => {
    const context = this.audio.audioContext;
    if (!context || this.finishing) return;
    if (this.snapshot.phase === 'paused') {
      this.rafId = requestAnimationFrame(this.pump);
      return;
    }

    // Advance the visual eighth between beats so the arrow moves on the "and".
    const metronome = this.metronome;
    if (metronome && this.snapshot.phase === 'running') {
      const elapsed = context.currentTime - metronome.startedAt;
      const eighthDuration = 30 / this.settings.bpm;
      const eighth = Math.max(0, Math.floor(elapsed / eighthDuration) % 8);
      if (eighth !== this.snapshot.currentEighth) this.store.update({ currentEighth: eighth });
    }

    const resolved = this.matcher.collect(context.currentTime);
    if (resolved.length > 0) {
      const last = resolved[resolved.length - 1];
      this.store.update({
        lastVerdict: last.verdict,
        score: this.matcher.score(),
        strokes: this.snapshot.strokes + resolved.filter((h) => h.onsetTime !== null).length,
      });
    }
    this.rafId = requestAnimationFrame(this.pump);
  };
}
